import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GraduationCapIcon, BookOpenIcon, HeartIcon, UsersIcon, ShieldCheckIcon, ClockIcon, DollarSignIcon, AwardIcon, CheckCircleIcon, ArrowRightIcon, StarIcon, QuoteIcon, CheckIcon } from 'lucide-react';
import type { FindDegreesTemplate } from '../data/findDegreesTemplates';
import { Logo } from './Logo';
import { GetInfoEmailForm, type GetInfoContactValues } from './GetInfoEmailForm';
const benefitIconMap: Record<string, React.ElementType> = {
  GraduationCapIcon,
  BookOpenIcon,
  HeartIcon,
  UsersIcon,
  ShieldCheckIcon,
  ClockIcon,
  DollarSignIcon,
  AwardIcon
};
interface DegreeLandingTemplateProps {
  data: FindDegreesTemplate;
  /**
   * `cta` sends the visitor onward to another page (the /find-degrees/ bridge
   * pages). `email` collects an email address in place and keeps the visitor on
   * this page (the /get-info/ pages).
   */
  mode?: 'cta' | 'email';
  /** In email mode, how much the page asks for before handing off. */
  collect?: 'email' | 'contact';
}

/**
 * The shared bridge-page layout: minimal top bar, hero with a conversion widget,
 * benefits, school list, testimonial, and a closing band. Both /find-degrees/
 * and /get-info/ render this from the same template data so the two families
 * stay visually identical and only the conversion step differs.
 */
export function DegreeLandingTemplate({
  data,
  mode = 'cta',
  collect = 'email'
}: DegreeLandingTemplateProps) {
  const navigate = useNavigate();
  const accent = data.accentColor;
  const accentDark = data.accentColorDark;
  const isEmailMode = mode === 'email';
  const handleCta = () => {
    navigate(data.ctaHref);
  };
  // With a destination set, the email carries the visitor onto the funnel along
  // with the program context this page already established. Without one, they
  // stay put and the field is replaced by a confirmation.
  const carriesForward = isEmailMode && Boolean(data.ctaHref);
  const handleEmailSubmitted = (values: GetInfoContactValues) => {
    if (!carriesForward) return;
    // Everything collected here rides along, so the funnel never re-asks for it.
    const params = new URLSearchParams();
    params.set('email', values.email);
    if (values.firstName) params.set('firstName', values.firstName);
    if (values.lastName) params.set('lastName', values.lastName);
    if (values.phone) params.set('phone', values.phone);
    const separator = data.ctaHref.includes('?') ? '&' : '?';
    navigate(`${data.ctaHref}${separator}${params.toString()}`);
  };
  return <main className="min-h-screen bg-white" data-id="element-170" data-mp="3r57vhfp8esj3">
      {/* Minimal Top Bar */}
      <div className="bg-white border-b border-gray-100" data-id="element-171" data-mp="3w2rc2m9ejhe7">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between" data-id="element-172" data-mp="3w5dkeo44a0xh">
          <Link to="/" className="flex items-center" aria-label="findonlinecolleges.org home" data-id="element-173" data-mp="1payxbfmd2k4x">
            <Logo data-id="element-174" data-mp="24f1qsjjkzji2" />
          </Link>
          <div className="hidden sm:flex items-center text-xs font-semibold text-gray-500 tracking-wider uppercase" data-id="element-175" data-mp="24g31gmwoq3z9">
            <ShieldCheckIcon className="w-4 h-4 mr-1.5 text-green-600" data-id="element-176" data-mp="1zex58f3tfykz" />
            Trusted by 2M+ Students
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-gray-50 relative overflow-hidden" data-id="element-177" data-mp="28fvcr7dgqo8b">
        {/* Tighter on phones so the widget is in reach without scrolling. */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-7 sm:py-14 lg:py-20" data-id="element-178" data-mp="2i783twrftada">
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-10 lg:gap-16 items-center" data-id="element-179" data-mp="2i9lvdvmws4g3">
            {/* Left: Copy */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="text-center lg:text-left" data-id="element-180" data-mp="2s5p0nad24j79">
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-widest text-white mb-4 lg:mb-6" style={{
              backgroundColor: '#4A3F8C'
            }} data-id="element-181" data-mp="z81wxqx11jfe">
                <span className="w-1.5 h-1.5 rounded-full bg-gold-light" data-id="element-182" data-mp="1d71fcgty5234" />
                {data.heroEyebrowBadge || data.eyebrow}
              </span>

              <h1 className="text-3xl sm:text-4xl lg:text-6xl font-extrabold leading-[1.15] lg:leading-[1.05] text-gray-900 mb-5 lg:mb-6 tracking-tight text-center lg:text-left text-balance" data-id="element-183" data-mp="kje2jnyx3fr1">
                {data.heroHeadlinePrefix && <>{data.heroHeadlinePrefix} </>}
                {data.heroHeadlineHighlight && <span style={{
                color: '#4A3F8C'
              }} data-id="element-184" data-mp="3t08vaam03jre">
                    {data.heroHeadlineHighlight}
                  </span>}
                {/* The suffix takes its own line on desktop, where the headline
                    is wide enough to carry two deliberate lines. On a phone it
                    wraps naturally instead of breaking mid-thought. */}
                {data.heroHeadlineSuffix && <>
                    <br className="hidden lg:inline" data-id="element-185" data-mp="3ceq91kur7fls" /> {data.heroHeadlineSuffix}
                  </>}
                {!data.heroHeadlinePrefix && !data.heroHeadlineHighlight && !data.heroHeadlineSuffix && data.headline}
              </h1>

              {/* Desktop only: on a phone this paragraph is the one block
                  standing between the headline and the widget. */}
              <p className="hidden lg:block text-lg text-gray-600 max-w-xl leading-relaxed" data-id="element-186" data-mp="2oq733bz6fewv">
                {data.subheadline}
              </p>
            </motion.div>

            {/* Right: Step indicator + Card */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} data-id="element-187" data-mp="2a1xazq267sv9">
              {/* Step Indicator */}
              <div className="flex items-center justify-between mb-5 px-2 sm:px-4" data-id="element-188" data-mp="2stq5xq0svo5m">
                <div className="flex items-center gap-2.5" data-id="element-189" data-mp="2xskvrbx6qcz8">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#3E7F71'
                }} data-id="element-190" data-mp="2xs1ezyj9tums">
                    <CheckIcon className="w-4 h-4 text-white" strokeWidth={3} data-id="element-191" data-mp="2o0ex5jcfgxb2" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900" data-id="element-192" data-mp="3gj29oadcz40j">
                    {data.heroStep1Label || 'Profile Complete'}
                  </span>
                </div>
                <div className="flex-1 h-px bg-gray-300 mx-3 sm:mx-4" data-id="element-193" data-mp="151z1c7kd4zyx" />
                <div className="flex items-center gap-2.5" data-id="element-194" data-mp="10477v942l5sm">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#4A3F8C'
                }} data-id="element-195" data-mp="1dwwxrompyuv0">
                    <span className="text-xs font-bold text-white" data-id="element-196" data-mp="m60mgsgb4y9u">2</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-500" data-id="element-197" data-mp="h7futhf4fmx0">
                    {data.heroStep2Label || 'Confirm Email'}
                  </span>
                </div>
              </div>

              {/* Card */}
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 sm:p-10 text-center" data-id="element-198" data-mp="q8x9cvcvrgjl">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-6" data-id="element-199" data-mp="26mvrvyfrti6j">
                  {data.heroCardHeading || data.headline}
                </h2>
                {isEmailMode ? <GetInfoEmailForm idPrefix="hero" submitLabel={data.ctaLabel} collect={collect} showConfirmation={!carriesForward} confirmation={carriesForward ? undefined : "We'll email your program details shortly. Keep reading below to see what to look for in a program."} onSubmitted={handleEmailSubmitted} data-id="element-200" data-mp="2lm9e1qz5aujj" /> : <button type="button" onClick={handleCta} className="group inline-flex items-center justify-center bg-cta hover:bg-cta-dark text-white font-bold py-3.5 px-8 rounded-lg text-base transition-colors duration-200 shadow-md" data-id="element-201" data-mp="3ed9ho0tkdwao">
                    {data.ctaLabel}
                    <ArrowRightIcon className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" data-id="element-202" data-mp="1ghcis6rt7vx7" />
                  </button>}
                {data.heroCardHelper && <p className="mt-6 text-xs sm:text-sm text-gray-500 leading-relaxed max-w-xs mx-auto" data-id="element-203" data-mp="16qq6alfe3liq">
                    {data.heroCardHelper}
                  </p>}
              </div>

              {/* Trust strip below card */}
              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-gray-500" data-id="element-204" data-mp="1zevuxj9kdunu">
                {/* Three is what fits on one line at this width — a fourth
                    wraps and turns a reassurance strip into a list. */}
                {data.trustItems.slice(0, 3).map(item => <div key={item} className="flex items-center" data-id="element-205" data-mp="eztvdqcw3qy3">
                    <CheckCircleIcon className="w-3.5 h-3.5 text-green-600 mr-1.5 flex-shrink-0" data-id="element-206" data-mp="f1enl9bqexss" />
                    {item}
                  </div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 lg:py-20 bg-white" data-id="element-207" data-mp="2jcbfvht9igmh">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-208" data-mp="2x2n9jl5hwnfq">
          <div className="text-center max-w-2xl mx-auto mb-12" data-id="element-209" data-mp="2x487zluxkqiv">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3" style={{
            color: accent
          }} data-id="element-210" data-mp="2sboq4cp7n989">
              {data.benefitsHeading}
            </h2>
            <div className="w-16 h-1 mx-auto rounded-full" style={{
            backgroundColor: '#C9752E'
          }} data-id="element-211" data-mp="320yizurvovxf" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-212" data-mp="3c0z6zu0p007y">
            {data.benefits.map((benefit, i) => {
            const Icon = benefitIconMap[benefit.icon] || BookOpenIcon;
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.05
            }} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow text-center" data-id="element-213" data-mp="1hnpt7q145sjw" data-mpi={i}>
                  <div className="w-12 h-12 mx-auto rounded-lg flex items-center justify-center mb-4" style={{
                backgroundColor: `${accent}15`
              }} data-id="element-214" data-mp="sqph8fywj485">
                    <Icon className="w-6 h-6" style={{
                  color: accent
                }} data-id="element-215" data-mp="298d7yibt526f" />
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{
                color: accent
              }} data-id="element-216" data-mp="2bsj96i91eeja">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-217" data-mp="1xryr5oay4mqu">
                    {benefit.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Featured Schools */}
      <section className="py-14 bg-gray-50 border-y border-gray-200" data-id="element-218" data-mp="2qhfrr98pz9y5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-219" data-mp="2low2vg4l0se1">
          <h2 className="text-center text-sm font-bold uppercase tracking-widest mb-8 text-gray-500" data-id="element-220" data-mp="2gqunb56d7b7b">
            {data.schoolsHeading}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-221" data-mp="3e7t9o6xlbsf0">
            {data.schoolNames.map(name => <div key={name} className="bg-white border border-gray-200 rounded-lg p-4 text-center flex items-center justify-center min-h-[80px]" data-id="element-222" data-mp="39dp2pnobrkdx">
                <span className="text-sm font-semibold leading-tight" style={{
              color: accent
            }} data-id="element-223" data-mp="3lru6uotgckjs">
                  {name}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      {data.testimonial && <section className="py-16 lg:py-20 bg-white" data-id="element-224" data-mp="8hmzmkvelq4e">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-225" data-mp="da7t1laee17e">
            <QuoteIcon className="w-12 h-12 mx-auto mb-6 opacity-30" style={{
          color: accent
        }} data-id="element-226" data-mp="dcahw1ztdoaj" />
            <div className="flex items-center justify-center gap-1 mb-4" data-id="element-227" data-mp="n9owdw26g307">
              {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className="w-5 h-5 fill-current text-gold" data-id="element-228" data-mp="r7mhamox02xs" />)}
            </div>
            <p className="text-xl sm:text-2xl font-medium leading-relaxed mb-6 italic" style={{
          color: accent
        }} data-id="element-229" data-mp="1jzim9a1rsygy">
              "{data.testimonial.quote}"
            </p>
            <p className="font-bold text-gray-900" data-id="element-230" data-mp="1tqd9agmuc23g">{data.testimonial.name}</p>
            <p className="text-sm text-gray-500" data-id="element-231" data-mp="15z7um4smzfuq">{data.testimonial.detail}</p>
          </div>
        </section>}

      {/* Bottom CTA */}
      <section className="py-16 lg:py-20" style={{
      backgroundColor: accentDark
    }} data-id="element-232" data-mp="1127qhzfckn69">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-233" data-mp="1ey1o3mao7q42">
          <h2 className="text-2xl sm:text-4xl font-extrabold text-white mb-4 leading-tight" data-id="element-234" data-mp="1a004n09c1os2">
            {data.bottomCtaHeading}
          </h2>
          <p className="text-base sm:text-lg text-gray-300 mb-8 leading-relaxed" data-id="element-235" data-mp="2lgzu7o4appie">
            {data.bottomCtaBody}
          </p>
          {isEmailMode ? <div className="max-w-md mx-auto" data-id="element-236" data-mp="2tw7blerh0ky6">
              <GetInfoEmailForm idPrefix="footer" tone="dark" submitLabel={data.bottomCtaLabel} collect={collect} showConfirmation={!carriesForward} confirmation={carriesForward ? undefined : "We'll email your program details shortly. No obligation to enroll."} onSubmitted={handleEmailSubmitted} data-id="element-237" data-mp="2xzlss8r5b7d7" />
            </div> : <button type="button" onClick={handleCta} className="group inline-flex items-center justify-center bg-cta hover:bg-cta-dark text-white font-bold py-4 px-10 rounded-lg text-base tracking-wide transition-colors duration-200 shadow-lg" data-id="element-238" data-mp="32xnk1gyq6czr">
              {data.bottomCtaLabel}
              <ArrowRightIcon className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" data-id="element-239" data-mp="e45dj5n3agu7" />
            </button>}
          <p className="mt-5 text-xs text-gray-400 uppercase tracking-widest" data-id="element-240" data-mp="94spihkhf3q1">
            Complementary • No Obligation • Takes 2 Minutes
          </p>
        </div>
      </section>

      {/* Footer mini */}
      <footer className="bg-gray-900 text-gray-400 text-xs py-6" data-id="element-241" data-mp="3cjsn4m3ps5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-2" data-id="element-242" data-mp="3rafa7ccmls1b">
          <p data-id="element-243" data-mp="3mjywrs8t26ey">
            &copy; {new Date().getFullYear()} FindOnlineColleges.org. All rights
            reserved.
          </p>
          <p className="max-w-3xl mx-auto leading-relaxed" data-id="element-244" data-mp="3hk46nqwutkuy">
            Advertiser Disclosure: FindOnlineColleges.org is an
            advertising-supported site. Featured schools compensate us, which
            does not influence our editorial content.
          </p>
          <div className="flex justify-center gap-4 pt-2" data-id="element-245" data-mp="33l4dq72b6jsz">
            <Link to="/privacy-policy" className="hover:text-white transition-colors" data-id="element-246" data-mp="33lmhrpv7q2bg">
              Privacy Policy
            </Link>
            <Link to="/terms-and-conditions" className="hover:text-white transition-colors" data-id="element-247" data-mp="1o13uf2dhgij8">
              Terms of Service
            </Link>
          </div>
        </div>
      </footer>
    </main>;
}